import dayjs from "dayjs";

/**
 * Batch Number Generator
 *
 * Generates unique batch numbers in the format:
 * {PRODUCT_CODE}-{START_MMDD}-{END_MMDD}
 *
 * Example: "ACEITE500-0106-0110" for product ACEITE 500ML from Jan 6 to Jan 10
 */

/**
 * Generate a batch number from product info and dates
 *
 * @param productCode - Product short name or SKU
 * @param startDate - Batch start date
 * @param endDate - Batch end date
 * @returns Formatted batch number
 */
export const generateBatchNumber = (
  productCode: string,
  startDate: Date | string,
  endDate: Date | string
): string => {
  // Clean the product code: remove spaces, special characters, keep only alphanumeric
  const cleanCode = productCode
    .replace(/[^A-Z0-9]/gi, "")
    .toUpperCase()
    .slice(0, 20); // Limit length

  // Format dates as MMDD
  const startMmdd = dayjs(startDate).format("MMDD");
  const endMmdd = dayjs(endDate).format("MMDD");

  return `${cleanCode}-${startMmdd}-${endMmdd}`;
};

/**
 * Parse a batch number back into its components
 *
 * @param batchNumber - The batch number to parse
 * @returns Object with productCode, startMmdd, endMmdd or null if invalid
 */
export const parseBatchNumber = (
  batchNumber: string
): {
  productCode: string;
  startMmdd: string;
  endMmdd: string;
} | null => {
  const parts = batchNumber.split("-");

  // Expected format: CODE-MMDD-MMDD (at least 3 parts, last two are dates)
  if (parts.length < 3) return null;

  const endMmdd = parts.pop()!;
  const startMmdd = parts.pop()!;
  const productCode = parts.join("-"); // In case product code has dashes

  // Validate date parts (should be 4 digits)
  if (!/^\d{4}$/.test(startMmdd) || !/^\d{4}$/.test(endMmdd)) {
    return null;
  }

  return {
    productCode,
    startMmdd,
    endMmdd,
  };
};

/**
 * Format batch number for display
 * Example: "ACEITE500-0106-0110" → "ACEITE500 (06-Ene al 10-Ene)"
 *
 * @param batchNumber - The batch number
 * @param year - Optional year for date context (defaults to current year)
 */
export const formatBatchNumberForDisplay = (
  batchNumber: string,
  year?: number
): string => {
  const parsed = parseBatchNumber(batchNumber);
  if (!parsed) return batchNumber;

  const currentYear = year || dayjs().year();

  // Convert MMDD to readable date
  const formatMmdd = (mmdd: string): string => {
    const month = parseInt(mmdd.slice(0, 2), 10);
    const day = parseInt(mmdd.slice(2, 4), 10);

    // Create a date to use dayjs formatting
    const date = dayjs(`${currentYear}-${month}-${day}`);
    return date.isValid() ? date.format("DD-MMM") : mmdd;
  };

  const startFormatted = formatMmdd(parsed.startMmdd);
  const endFormatted = formatMmdd(parsed.endMmdd);

  return `${parsed.productCode} (${startFormatted} al ${endFormatted})`;
};

/**
 * Get date range from batch number
 *
 * @param batchNumber - The batch number
 * @param year - Year to use for the dates
 * @returns Object with startDate and endDate as dayjs objects, or null
 */
export const getBatchDateRange = (
  batchNumber: string,
  year: number
): {
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
} | null => {
  const parsed = parseBatchNumber(batchNumber);
  if (!parsed) return null;

  const startMonth = parseInt(parsed.startMmdd.slice(0, 2), 10);
  const startDay = parseInt(parsed.startMmdd.slice(2, 4), 10);
  const endMonth = parseInt(parsed.endMmdd.slice(0, 2), 10);
  const endDay = parseInt(parsed.endMmdd.slice(2, 4), 10);

  const startDate = dayjs(`${year}-${startMonth}-${startDay}`);
  const endDate = dayjs(`${year}-${endMonth}-${endDay}`);

  if (!startDate.isValid() || !endDate.isValid()) {
    return null;
  }

  return { startDate, endDate };
};

/**
 * Check if a batch number already exists in a list
 * Used to ensure uniqueness when creating batches
 */
export const isBatchNumberUnique = (
  batchNumber: string,
  existingBatchNumbers: string[]
): boolean => {
  return !existingBatchNumbers.includes(batchNumber);
};

/**
 * Suggest alternative batch number if original already exists
 * Appends a suffix like -A, -B, etc.
 */
export const suggestAlternativeBatchNumber = (
  batchNumber: string,
  existingBatchNumbers: string[]
): string => {
  if (isBatchNumberUnique(batchNumber, existingBatchNumbers)) {
    return batchNumber;
  }

  const suffixes = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  for (const suffix of suffixes) {
    const alternative = `${batchNumber}-${suffix}`;
    if (isBatchNumberUnique(alternative, existingBatchNumbers)) {
      return alternative;
    }
  }

  // Fallback: add timestamp
  return `${batchNumber}-${Date.now()}`;
};
